import { useEffect, useState, useRef } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CalendarDays, Clock, Link2, Unlink, CheckCircle2, Video, Users, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 60_000;

const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultMeetingForm = () => {
    const now = new Date();
    const start = new Date(now.getTime() + 60 * 60 * 1000);
    start.setMinutes(0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    return {
        title: "",
        description: "",
        start: toLocalInput(start.toISOString()),
        end: toLocalInput(end.toISOString()),
        location: "",
        attendees: "",
        create_meet: true,
    };
};

export default function CalendarPage() {
    const { hasPermission } = useAuth();
    const canManage = hasPermission("calendar_manage");
    const [tasks, setTasks] = useState([]);
    const [selected, setSelected] = useState(new Date());
    const [gcal, setGcal] = useState({ connected: false, configured: true, email: null });
    const [busy, setBusy] = useState(false);
    const [meetOpen, setMeetOpen] = useState(false);
    const [meetForm, setMeetForm] = useState(defaultMeetingForm());
    const [creating, setCreating] = useState(false);
    const pollRef = useRef(null);

    // Calendar shows ONLY Google Calendar events. CRM-only tasks live in /tasks.
    // The backend already classifies docs via `source`; we request gcal-only.
    const loadTasks = async () => {
        try {
            const { data } = await api.get("/calendar/task-events");
            setTasks(data);
        } catch {}
    };

    // Pull Google events into CRM as tasks (silent, idempotent via gcal_event_id)
    const pullFromGoogle = async () => {
        if (!canManage) return;
        try { await api.post("/calendar/pull"); } catch {}
    };

    const syncAll = async () => {
        await pullFromGoogle();
        await loadTasks();
    };

    const loadStatus = async () => {
        try {
            const { data } = await api.get("/oauth/calendar/status");
            setGcal(data);
            return data;
        } catch { return { connected: false }; }
    };

    useEffect(() => {
        (async () => {
            await loadTasks();
            const st = await loadStatus();
            if (st.connected && canManage) await syncAll();
        })();
        const params = new URLSearchParams(window.location.search);
        if (params.get("gcal") === "connected") {
            toast.success("Google Calendar conectado");
            window.history.replaceState({}, "", "/calendar");
        }
        /* eslint-disable-next-line */
    }, []);

    // Background polling when connected (silent sync, no toasts).
    useEffect(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (!gcal.connected || !canManage) return;
        pollRef.current = setInterval(() => { syncAll(); }, POLL_MS);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
        /* eslint-disable-next-line */
    }, [gcal.connected, canManage]);

    const connectGoogle = async () => {
        if (!canManage) return;
        setBusy(true);
        try {
            const { data } = await api.get("/oauth/calendar/connect");
            window.location.href = data.authorization_url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "No se pudo iniciar la conexión");
            setBusy(false);
        }
    };

    const disconnectGoogle = async () => {
        if (!canManage) return;
        if (!window.confirm("¿Desconectar Google Calendar?")) return;
        try {
            await api.post("/oauth/calendar/disconnect");
            toast.success("Desconectado");
            await loadStatus();
            await loadTasks();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const openNewMeeting = () => {
        if (!canManage) return;
        if (!gcal.connected) {
            toast.error("Conecta Google Calendar primero");
            return;
        }
        setMeetForm(defaultMeetingForm());
        setMeetOpen(true);
    };

    const createMeeting = async () => {
        if (!canManage) return;
        if (!meetForm.title.trim()) { toast.error("Título obligatorio"); return; }
        if (!meetForm.start || !meetForm.end) { toast.error("Fecha de inicio y fin obligatorias"); return; }
        const startDate = new Date(meetForm.start);
        const endDate = new Date(meetForm.end);
        if (!(endDate > startDate)) { toast.error("La fecha fin debe ser posterior al inicio"); return; }
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        // RFC3339 with offset matching the user's tz (Google requires it when timeZone is set).
        const pad = (n) => String(n).padStart(2, "0");
        const toLocalRFC = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        const startStr = toLocalRFC(startDate);
        const endStr = toLocalRFC(endDate);
        const attendees = meetForm.attendees
            .split(/[\s,;]+/)
            .map(s => s.trim())
            .filter(s => s.includes("@"));
        setCreating(true);
        try {
            await api.post("/calendar/events", {
                title: meetForm.title.trim(),
                description: meetForm.description.trim() || undefined,
                location: meetForm.location.trim() || undefined,
                start: startStr,
                end: endStr,
                timezone: tz,
                attendees,
                create_meet: !!meetForm.create_meet,
            });
            toast.success("Reunión creada en Google Calendar");
            setMeetOpen(false);
            await syncAll();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (formatApiError(detail) || "No se pudo crear la reunión"));
        } finally {
            setCreating(false);
        }
    };

    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    const dayTasks = tasks.filter(t => t.due_date && sameDay(new Date(t.due_date), selected));
    const taskDays = tasks.filter(t => t.due_date).map(t => new Date(t.due_date));

    return (
        <Layout
            title="Calendario"
            subtitle="Reuniones de Google Calendar"
            actions={
                <div className="flex items-center gap-2 flex-wrap" data-testid="calendar-actions">
                    {gcal.connected ? (
                        <>
                            <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded flex items-center gap-1" data-testid="gcal-connected">
                                <CheckCircle2 className="w-3.5 h-3.5"/>Google: {gcal.email || "Conectado"}{canManage ? " · sync auto" : ""}
                            </span>
                            {canManage && <><Button onClick={openNewMeeting} data-testid="calendar-new-meeting"><Plus className="w-4 h-4 mr-2"/>Nueva reunión</Button>
                            <Button variant="outline" size="sm" onClick={disconnectGoogle} data-testid="gcal-disconnect" className="text-red-600 hover:text-red-700"><Unlink className="w-4 h-4 mr-2"/>Desconectar</Button></>}
                        </>
                    ) : canManage ? (
                        <Button onClick={connectGoogle} disabled={busy || !gcal.configured} data-testid="gcal-connect-btn"><Link2 className="w-4 h-4 mr-2"/>Conectar Google Calendar</Button>
                    ) : null}
                </div>
            }
            data-testid="calendar-page"
        >
            <div className="grid lg:grid-cols-3 gap-6">
                <div className="bg-white border border-slate-200 rounded-lg p-4 lg:col-span-1 flex justify-center">
                    <Calendar
                        mode="single"
                        selected={selected}
                        onSelect={(d) => d && setSelected(d)}
                        modifiers={{ withTask: taskDays }}
                        modifiersClassNames={{
                            withTask: "bg-orange-100 text-orange-900 font-bold",
                        }}
                        data-testid="calendar-widget"
                    />
                </div>
                <div className="lg:col-span-2 space-y-4">
                    <h3 className="font-display font-semibold text-slate-900">
                        {selected.toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" })}
                    </h3>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Reuniones</div>
                        <div className="space-y-2">
                            {dayTasks.length === 0 && (
                                <div className="text-sm text-slate-400 py-4 text-center bg-white border border-slate-200 rounded-lg">
                                    Sin reuniones para este día
                                </div>
                            )}
                            {dayTasks.map((t) => {
                                const endDate = t.end_date ? new Date(t.end_date) : null;
                                return (
                                    <div key={t.task_id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3" data-testid={`calendar-task-${t.task_id}`}>
                                        <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center mt-0.5"><CalendarDays className="w-5 h-5"/></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-slate-900">{t.title}</div>
                                            {t.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>}
                                            <div className="text-xs text-slate-500 flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
                                                <span className="flex items-center gap-1"><Clock className="w-3 h-3"/>
                                                    {new Date(t.due_date).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}
                                                    {endDate && ` – ${endDate.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}`}
                                                </span>
                                                {t.gcal_event_id && <span className="text-blue-600 inline-flex items-center gap-1" title="Sincronizado con Google Calendar"><CheckCircle2 className="w-3 h-3"/>En Google</span>}
                                                {t.meet_link && (
                                                    <a href={t.meet_link} target="_blank" rel="noreferrer"
                                                        className="text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded inline-flex items-center gap-1 transition-colors"
                                                        data-testid={`calendar-task-meet-${t.task_id}`}>
                                                        <Video className="w-3 h-3"/>Unirse
                                                    </a>
                                                )}
                                                {Array.isArray(t.attendees) && t.attendees.length > 0 && (
                                                    <span className="flex items-center gap-1 text-slate-600" title={t.attendees.map(a=>a.email||a).join(", ")}>
                                                        <Users className="w-3 h-3"/>{t.attendees.length} invitado{t.attendees.length>1?"s":""}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600 mt-1">{t.priority}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={meetOpen} onOpenChange={setMeetOpen}>
                <DialogContent className="max-w-lg" data-testid="meeting-dialog">
                    <DialogHeader>
                        <DialogTitle>Nueva reunión en Google Calendar</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Título *</Label>
                            <Input
                                value={meetForm.title}
                                onChange={(e) => setMeetForm({ ...meetForm, title: e.target.value })}
                                data-testid="meeting-title"
                                placeholder="Reunión con cliente"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Inicio *</Label>
                                <Input
                                    type="datetime-local"
                                    value={meetForm.start}
                                    onChange={(e) => setMeetForm({ ...meetForm, start: e.target.value })}
                                    data-testid="meeting-start"
                                />
                            </div>
                            <div>
                                <Label>Fin *</Label>
                                <Input
                                    type="datetime-local"
                                    value={meetForm.end}
                                    onChange={(e) => setMeetForm({ ...meetForm, end: e.target.value })}
                                    data-testid="meeting-end"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Ubicación</Label>
                            <Input
                                value={meetForm.location}
                                onChange={(e) => setMeetForm({ ...meetForm, location: e.target.value })}
                                placeholder="Opcional"
                            />
                        </div>
                        <div>
                            <Label>Invitados (emails separados por coma)</Label>
                            <Input
                                value={meetForm.attendees}
                                onChange={(e) => setMeetForm({ ...meetForm, attendees: e.target.value })}
                                placeholder="ana@empresa.com, juan@empresa.com"
                                data-testid="meeting-attendees"
                            />
                        </div>
                        <div>
                            <Label>Descripción</Label>
                            <Textarea
                                rows={3}
                                value={meetForm.description}
                                onChange={(e) => setMeetForm({ ...meetForm, description: e.target.value })}
                            />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer">
                            <Checkbox
                                checked={!!meetForm.create_meet}
                                onCheckedChange={(v) => setMeetForm({ ...meetForm, create_meet: !!v })}
                                data-testid="meeting-create-meet"
                            />
                            Generar enlace de Google Meet automáticamente
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setMeetOpen(false)} disabled={creating}>Cancelar</Button>
                        <Button onClick={createMeeting} disabled={creating} data-testid="meeting-save">
                            {creating ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</>
                            ) : (
                                <><Plus className="w-4 h-4 mr-2" />Crear reunión</>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
